// Mechanically extracted from frozen iOS58. Regenerate with docs/notifications-20261009/generate-reference.cjs.
const localeTag = (locale) => (locale === 'en' ? 'en-US' : 'ko-KR');
const formatLocalizedDate = (date, options, locale) =>
  date.toLocaleDateString(localeTag(locale), options);
const trustedWebHosts = new Set([
  'postpop.kr',
  'www.postpop.kr',
  'sandbox.postpop.kr',
  'postpop-sandbox.kro.kr',
]);
function decodePathPart(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
export function notificationPath(link) {
  const value = link?.trim();
  if (!value) return null;
  try {
    if (/^https?:\/\//i.test(value)) {
      const url = new URL(value);
      if (!trustedWebHosts.has(url.hostname.toLowerCase())) return null;
      return `${url.pathname}${url.search}`;
    }
    if (value.startsWith('postpop://')) {
      const url = new URL(value);
      const host = url.hostname ? `/${url.hostname}` : '';
      return `${host}${url.pathname}${url.search}` || '/';
    }
  } catch {
    return null;
  }
  return value.startsWith('/') ? value : `/${value}`;
}
export function notificationPathParts(link) {
  const path = notificationPath(link);
  if (!path) return [];
  return path.split('?')[0].split('/').filter(Boolean).map(decodePathPart);
}
export function resolveNotificationLink(link) {
  const path = notificationPath(link);
  if (!path) return null;
  const [pathname, search = ''] = path.split('?');
  const parts = pathname.split('/').filter(Boolean).map(decodePathPart);
  if (!parts.length) return { name: 'MainTabs', params: { screen: 'Home' } };
  if (parts[0] === 'notifications') return { name: 'Notifications' };
  if (parts.length === 1 && parts[0] === 'sponsorship') return { name: 'Sponsorship' };
  if (parts[0] === 'reviews') {
    if (parts[1] === 'local' && parts[2] && parts[3] === 'user-reviews' && parts[4]) {
      const albumSlug = `local/${parts[2]}`;
      const reviewId = parts[4];
      if (parts[5] === 'comment' && parts[6]) {
        return { name: 'UserReviewComment', params: { albumSlug, reviewId, commentId: parts[6] } };
      }
      return { name: 'UserReviewDetail', params: { albumSlug, reviewId } };
    }
    if (parts[1] && parts[2] === 'user-reviews' && parts[3]) {
      const albumSlug = parts[1];
      const reviewId = parts[3];
      if (parts[4] === 'comment' && parts[5]) {
        return { name: 'UserReviewComment', params: { albumSlug, reviewId, commentId: parts[5] } };
      }
      return { name: 'UserReviewDetail', params: { albumSlug, reviewId } };
    }
    if (parts[1] === 'local' && parts[2]) {
      return { name: 'LegacyAlbumDetail', params: { albumSource: 'local', sourceId: parts[2] } };
    }
    if (parts[1]) return { name: 'AlbumDetail', params: { albumSlug: parts[1] } };
    return null;
  }
  if (parts[0] === 'lists' && parts[1]) {
    if (parts[2] === 'comment' && parts[3]) {
      return { name: 'PlaylistComment', params: { slug: parts[1], commentId: parts[3] } };
    }
    return { name: 'PlaylistDetail', params: { slug: parts[1] } };
  }
  if (parts[0] === 'users' && parts[1]) {
    return { name: 'UserProfile', params: { nickname: parts[1] } };
  }
  if (parts[0] === 'mypage') return { name: 'MainTabs', params: { screen: 'My' } };
  if (parts[0] === 'explore') return { name: 'MainTabs', params: { screen: 'Explore' } };
  if (parts[0] === 'albums') {
    const query = new URLSearchParams(search);
    const category = query.get('category');
    const region = query.get('region');
    return {
      name: 'AlbumList',
      params: {
        category: category === 'recommended' ? 'recommended' : 'releases',
        region: region === 'kr' || region === 'global' ? region : 'all',
      },
    };
  }
  if (parts[0] === 'news' && parts[1]) {
    if (parts[2] === 'comment' && parts[3]) {
      return { name: 'NewsComment', params: { slug: parts[1], commentId: parts[3] } };
    }
    return { name: 'NewsDetail', params: { slug: parts[1] } };
  }
  if (parts[0] === 'notice') {
    if (!parts[1]) return { name: 'NoticeList' };
    if (parts[2] === 'comment' && parts[3]) {
      return { name: 'NoticeComment', params: { slug: parts[1], commentId: parts[3] } };
    }
    return { name: 'NoticeDetail', params: { slug: parts[1] } };
  }
  return null;
}
const notificationLabel = {
  FOLLOW: 'notifications.label.follow',
  FOLLOWING_REVIEW_CREATED: 'notifications.label.followingReview',
  FOLLOWING_PLAYLIST_CREATED: 'notifications.label.followingPlaylist',
  REVIEW_LIKE: 'notifications.label.reviewLike',
  COMMENT_LIKE: 'notifications.label.commentLike',
  COMMENT_ON_REVIEW: 'notifications.label.reviewComment',
  COMMENT_ON_LIST: 'notifications.label.playlistComment',
  COMMENT_ON_NEWS: 'notifications.label.newsComment',
  COMMENT_ON_NOTICE: 'notifications.label.noticeComment',
  REPLY_ON_COMMENT: 'notifications.label.reply',
  GRADE_CHANGE: 'notifications.label.gradeChange',
  REVIEW_FEATURED: 'notifications.label.featuredReview',
  PROFILE_IMAGE_DELETED: 'notifications.label.profileImageDeleted',
  BIO_DELETED: 'notifications.label.bioDeleted',
  COMMENT_DELETED: 'notifications.label.commentDeleted',
  REVIEW_DELETED: 'notifications.label.reviewDeleted',
  PLAYLIST_DELETED: 'notifications.label.playlistDeleted',
  NEW_RELEASES: 'notifications.label.newReleases',
  TASTE_DISCOVERY: 'notifications.label.tasteDiscovery',
  SAVED_ALBUM_REMINDER: 'notifications.label.savedAlbumReminder',
  MUSIC_ARCHIVE_SUMMARY: 'notifications.label.musicArchive',
  FIRST_RECORD_REMINDER: 'notifications.label.firstRecord',
  REENGAGEMENT: 'notifications.label.reengagement',
};
const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
const parseNotificationDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const getNotificationGroup = (value, locale, t, now = new Date()) => {
  const date = parseNotificationDate(value);
  if (!date) return { key: 'earlier', label: t('notifications.group.earlier') };
  const age = Math.floor((startOfLocalDay(now) - startOfLocalDay(date)) / 86400000);
  if (age <= 0) return { key: 'today', label: t('notifications.group.today') };
  if (age === 1) return { key: 'yesterday', label: t('notifications.group.yesterday') };
  if (age <= 6) return { key: 'recent-week', label: t('notifications.group.recentWeek') };
  const key = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  const label = formatLocalizedDate(
    date,
    date.getFullYear() === now.getFullYear()
      ? { month: 'long', day: 'numeric' }
      : { year: 'numeric', month: 'long', day: 'numeric' },
    locale,
  );
  return { key, label };
};
const formatNotificationTime = (value, locale) => {
  const date = parseNotificationDate(value);
  if (!date) return value;
  return date.toLocaleTimeString(localeTag(locale), { hour: 'numeric', minute: '2-digit' });
};
const actorNickname = (item, t) => {
  const actorParts = notificationPathParts(item.actorProfileLink);
  if (actorParts[0] === 'users' && actorParts[1]) return actorParts[1];
  return item.actorNicknameSnapshot?.trim() || t('notifications.someone');
};
const quotedTarget = (item, fallback) => {
  const target = item.targetTitleSnapshot?.trim();
  return target ? `“${target}”` : fallback;
};
function notificationBody(item, t, locale) {
  const actor = actorNickname(item, t);
  const preview =
    (locale === 'en' ? item.contentPreviewEn : item.contentPreview)?.trim() ||
    item.contentPreview?.trim();
  const previewSuffix = preview ? `: ${preview}` : '.';
  const reasonSuffix = item.reason ? `: ${item.reason}` : '.';
  switch (item.type) {
    case 'FOLLOW':
      return t('notifications.body.follow', { actor });
    case 'FOLLOWING_REVIEW_CREATED':
      return t('notifications.body.followingReview', {
        actor,
        target: quotedTarget(item, t('notifications.newAlbum')),
      });
    case 'FOLLOWING_PLAYLIST_CREATED':
      return t('notifications.body.followingPlaylist', {
        actor,
        target: quotedTarget(item, t('notifications.newPlaylist')),
      });
    case 'REVIEW_LIKE':
      return t('notifications.body.reviewLike', {
        actor,
        target: quotedTarget(item, t('notifications.yourReview')),
      });
    case 'COMMENT_LIKE':
      return t('notifications.body.commentLike', { actor, suffix: previewSuffix });
    case 'COMMENT_ON_REVIEW':
      return t('notifications.body.reviewComment', {
        actor,
        target: quotedTarget(item, t('notifications.yourReview')),
        suffix: previewSuffix,
      });
    case 'COMMENT_ON_LIST':
      return t('notifications.body.playlistComment', { actor, suffix: previewSuffix });
    case 'COMMENT_ON_NEWS':
      return t('notifications.body.newsComment', {
        actor,
        target: quotedTarget(item, t('notifications.news')),
        suffix: previewSuffix,
      });
    case 'COMMENT_ON_NOTICE':
      return t('notifications.body.noticeComment', {
        actor,
        target: quotedTarget(item, t('notifications.notice')),
        suffix: previewSuffix,
      });
    case 'REPLY_ON_COMMENT':
      return t('notifications.body.reply', { actor, suffix: previewSuffix });
    case 'GRADE_CHANGE':
      return item.newGrade
        ? t('notifications.body.gradeChanged', { grade: item.newGrade })
        : t('notifications.body.gradeChangedUnknown');
    case 'REVIEW_FEATURED':
      return t('notifications.body.featuredReview', {
        target: quotedTarget(item, t('notifications.yourReview')),
      });
    case 'PROFILE_IMAGE_DELETED':
      return t('notifications.body.profileImageDeleted', { suffix: reasonSuffix });
    case 'BIO_DELETED':
      return t('notifications.body.bioDeleted', { suffix: reasonSuffix });
    case 'COMMENT_DELETED':
      return t('notifications.body.commentDeleted', { suffix: reasonSuffix });
    case 'REVIEW_DELETED':
      return t('notifications.body.reviewDeleted', {
        target: quotedTarget(item, t('notifications.review')),
        suffix: reasonSuffix,
      });
    case 'PLAYLIST_DELETED':
      return t('notifications.body.playlistDeleted', {
        target: quotedTarget(item, t('notifications.playlist')),
        suffix: reasonSuffix,
      });
    case 'NEW_RELEASES':
    case 'TASTE_DISCOVERY':
    case 'SAVED_ALBUM_REMINDER':
    case 'MUSIC_ARCHIVE_SUMMARY':
    case 'FIRST_RECORD_REMINDER':
    case 'REENGAGEMENT': {
      if (locale === 'ko')
        return (
          item.reason?.trim() ||
          preview ||
          item.targetTitleSnapshot?.trim() ||
          item.newGrade?.trim() ||
          t('notifications.newDefault')
        );
      // These are generated reminders. Older rows have no English preview;
      // member comment previews in the cases above remain verbatim.
      if (item.contentPreviewEn?.trim()) return item.contentPreviewEn.trim();
      const key = {
        NEW_RELEASES: 'notifications.body.newReleases',
        TASTE_DISCOVERY: 'notifications.body.tasteDiscovery',
        SAVED_ALBUM_REMINDER: 'notifications.body.savedAlbumReminder',
        MUSIC_ARCHIVE_SUMMARY: 'notifications.body.musicArchive',
        FIRST_RECORD_REMINDER: 'notifications.body.firstRecord',
        REENGAGEMENT: 'notifications.body.reengagement',
      };
      const target = item.targetTitleSnapshot?.trim();
      return [target ? `“${target}”` : '', t(key[item.type])].filter(Boolean).join(' · ');
    }
    default:
      return (
        item.reason?.trim() ||
        preview ||
        item.targetTitleSnapshot?.trim() ||
        item.newGrade?.trim() ||
        t('notifications.newDefault')
      );
  }
}
const notificationDestination = (item) => {
  if (item.type === 'FOLLOW') return item.actorProfileLink || item.link;
  return item.link;
};

export {
  notificationLabel,
  getNotificationGroup,
  formatNotificationTime,
  notificationBody,
  notificationDestination,
};
